import React, { useState } from 'react';
import { Player } from '../../types';

export interface TeamSuggestion {
  team1: Player[];
  team2: Player[];
  subs: Player[];
  team1Skill: number;
  team2Skill: number;
  skillDiff: number;
}

interface TeamSuggestionsModalProps {
  suggestions: TeamSuggestion[];
  team1Name: string;
  team2Name: string;
  onApply: (suggestion: TeamSuggestion) => void;
  onRefresh: () => void;
  onClose: () => void;
  loading?: boolean;
  showSkillWarning?: boolean;
}

const GROUP_COLORS = [
  '#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#FF922B',
  '#CC5DE8', '#20C997', '#F06595', '#74C0FC', '#A9E34B',
];

function colorForGroup(groupId: string): string {
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash * 31 + groupId.charCodeAt(i)) & 0xffffffff;
  }
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
}

// Umbral "alto" (cm). Para repartir parejo a los altos entre los dos equipos.
const TALL_CM = 180;
const isTall = (p: any): boolean => (((p?.user?.heightCm ?? p?.heightCm) ?? 0) as number) >= TALL_CM;
const tallCount = (players: Player[]): number => players.filter(isTall).length;

const PlayerRow: React.FC<{ player: Player }> = ({ player }) => {
  const gc = player.friendGroupId ? colorForGroup(player.friendGroupId) : null;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 6px',
      borderRadius: '6px',
      background: 'rgba(255,255,255,0.04)',
      marginBottom: '3px',
    }}>
      {gc && (
        <span style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: gc,
          flexShrink: 0,
          display: 'inline-block',
        }} title="Grupo de amigos" />
      )}
      <span style={{ color: '#E0E0E0', fontSize: '13px', flex: 1 }}>{player.name}</span>
      {isTall(player) && <span style={{ fontSize: '11px' }} title="Alto (1,80m+)">📏</span>}
      <span style={{ color: '#78909C', fontSize: '11px' }}>{player.position}</span>
    </div>
  );
};

export const TeamSuggestionsModal: React.FC<TeamSuggestionsModalProps> = ({
  suggestions,
  team1Name,
  team2Name,
  onApply,
  onRefresh,
  onClose,
  loading,
  showSkillWarning = false,
}) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const suggestion = suggestions[selectedIdx];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: '16px',
    }}>
      <div data-testid="team-suggestions-modal" style={{
        background: '#23272A',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: '#FFFFFF', fontSize: '18px' }}>Sugerencias de Equipos</h2>
          <button
            data-testid="suggestions-close-btn"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#B0BEC5',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px',
              fontFamily: 'inherit',
            }}
          >
            ✕
          </button>
        </div>

        {/* Skill warning banner — when organizer hasn't set manual skills */}
        {showSkillWarning && (
          <div
            data-testid="skill-warning-banner"
            style={{
              padding: '10px 12px',
              marginBottom: '14px',
              borderRadius: '8px',
              background: 'rgba(255,167,38,0.1)',
              border: '1px solid rgba(255,167,38,0.35)',
              color: '#FFB74D',
              fontSize: '13px',
              lineHeight: 1.4,
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-start',
            }}
          >
            <span style={{ fontSize: '16px', lineHeight: 1 }}>💡</span>
            <span>
              Para sugerencias más parejas, ponele un nivel privado (1-10) a cada jugador desde su tarjeta.
              Por ahora estamos usando el rating general.
            </span>
          </div>
        )}

        {/* Tabs for suggestions */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              data-testid={`suggestion-tab-${i}`}
              onClick={() => setSelectedIdx(i)}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontWeight: 600,
                fontSize: '13px',
                background: selectedIdx === i ? '#00E676' : 'rgba(255,255,255,0.1)',
                color: selectedIdx === i ? '#181A1B' : '#B0BEC5',
                transition: 'background 0.15s',
              }}
            >
              Opción {i + 1}
            </button>
          ))}
        </div>

        {suggestion && (
          <>
            {/* Skill diff indicator */}
            <div data-testid="suggestion-skill-summary" style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              flexWrap: 'wrap',
            }}>
              <span style={{ color: '#4CAF50', fontWeight: 600, fontSize: '13px' }}>
                {team1Name}: {suggestion.team1Skill.toFixed(1)} pts
              </span>
              <span style={{ color: '#2196F3', fontWeight: 600, fontSize: '13px' }}>
                {team2Name}: {suggestion.team2Skill.toFixed(1)} pts
              </span>
              <span style={{
                color: suggestion.skillDiff <= 3 ? '#66BB6A' : suggestion.skillDiff <= 6 ? '#FFA726' : '#EF5350',
                fontWeight: 600,
                fontSize: '13px',
              }}>
                Diferencia: {suggestion.skillDiff.toFixed(1)}
              </span>
            </div>

            {/* Altura: cuántos "altos" (1,80m+) por equipo, para repartirlos parejo. */}
            {(() => {
              const t1 = tallCount(suggestion.team1);
              const t2 = tallCount(suggestion.team2);
              if (t1 === 0 && t2 === 0) return null;
              const uneven = Math.abs(t1 - t2) >= 2;
              return (
                <div style={{
                  fontSize: '12px',
                  color: uneven ? '#FFA726' : '#90A4AE',
                  marginBottom: '12px',
                }}>
                  📏 Altos: {team1Name} {t1} · {team2Name} {t2}{uneven ? ' ⚠️ desparejo' : ''}
                </div>
              );
            })()}

            {/* Teams */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#4CAF50',
                  marginBottom: '6px',
                  paddingBottom: '4px',
                  borderBottom: '1px solid rgba(76,175,80,0.3)',
                }}>
                  {team1Name} ({suggestion.team1.length})
                </div>
                {suggestion.team1.map(p => <PlayerRow key={p.id} player={p} />)}
              </div>
              <div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#2196F3',
                  marginBottom: '6px',
                  paddingBottom: '4px',
                  borderBottom: '1px solid rgba(33,150,243,0.3)',
                }}>
                  {team2Name} ({suggestion.team2.length})
                </div>
                {suggestion.team2.map(p => <PlayerRow key={p.id} player={p} />)}
              </div>
            </div>

            {suggestion.subs.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#78909C',
                  marginBottom: '6px',
                  paddingBottom: '4px',
                  borderBottom: '1px solid rgba(120,144,156,0.3)',
                }}>
                  Suplentes ({suggestion.subs.length})
                </div>
                {suggestion.subs.map(p => <PlayerRow key={p.id} player={p} />)}
              </div>
            )}

            {/* Legend */}
            <div style={{ fontSize: '11px', color: '#546E7A', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#1DE9B6', display: 'inline-block' }} />
              Jugadores del mismo color están vinculados como amigos
            </div>
          </>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            data-testid="suggestion-apply-btn"
            onClick={() => suggestion && onApply(suggestion)}
            disabled={!suggestion || loading}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              background: '#00E676',
              color: '#181A1B',
              fontWeight: 700,
              fontSize: '14px',
              cursor: suggestion && !loading ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              opacity: !suggestion || loading ? 0.6 : 1,
            }}
          >
            Aplicar esta sugerencia
          </button>
          <button
            data-testid="suggestion-refresh-btn"
            onClick={onRefresh}
            disabled={loading}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.05)',
              color: '#B0BEC5',
              fontWeight: 700,
              fontSize: '14px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Generando...' : 'Generar nuevas'}
          </button>
        </div>
      </div>
    </div>
  );
};
